import { Injectable } from '@angular/core';
import pLimit from 'p-limit';
import { LogService } from '../baseServices/logging/log.service';
import { GlobalConstants } from '../constant/constant';
import { FORM_CONTROL_TYPES } from '../enums/general.enum';
import ChoiceDefinition from '../models/choice-definition/choice-definition';
import { ChoiceDefinitionMetadata } from '../models/choice-definition/choice-definition-metadata';
import { ChoiceDefinitionSyncResponse } from '../models/form-definition/choice-definition-sync-response';
import FormControl from '../models/form-definition/form-control';
import FormDefinition from '../models/form-definition/form-definition';
import { NotificationsService } from '../services/notifications.service';
import { RecordNotFoundException } from '../utils/exceptions';
import { ChoiceDefinitionsService } from './choice-definitions.service';
import { DataService } from './data.service';
import { FormDefinitionsService } from './form-definitions.service';

/** Internal request shape for choice sync. */
interface ChoiceSyncRequest {
  ChoiceDefinitionId: number;
  ProjectIds: number[];           // All projects whose forms reference this choice definition
  LatestModifiedDate?: string;    // Most recent modified date among existing stored options (for incremental sync)
  CurrentChoiceIds: number[];     // Local IDs we already have (used by API for pruning)
}


@Injectable({
  providedIn: 'root'
})
export class SyncChoiceDefinitionService extends DataService {
  // Concurrency limit for outbound choice definition fetch calls. Note that browser may impose its own limits.
  private static readonly CHOICE_SYNC_CONCURRENCY = 10;
  // Prevent duplicate concurrent retry executions from alert button spamming
  private choiceDefinitionRetryInProgress = false;

  constructor(
    private formDefinitionsService: FormDefinitionsService,
    private choiceDefinitionsService: ChoiceDefinitionsService,
    private logService: LogService,
    private notificationService: NotificationsService
  ) { super(); }

  /**
   * Synchronize choice definitions used by any form definition.
   *
   * @param choiceDefinitionId  When provided, limits sync to that specific choice definition (if referenced).
   * @param fullSync            When true, sends bare requests without local enrichment so server returns all data.
   */
  async sync({
    choiceDefinitionId = null,
    fullSync = false
  }: { choiceDefinitionId?: number | null; fullSync?: boolean } = {}): Promise<void> {

    const formDefinitions = await this.formDefinitionsService.getAll();
    let syncRequests = this.buildSyncRequests(formDefinitions);

    if (choiceDefinitionId != null) {
      syncRequests = syncRequests.filter(request => request.ChoiceDefinitionId === choiceDefinitionId);
    }

    if (!syncRequests.length) {
      this.logService.debug('SyncChoiceDefinition.NoSyncNeeded', { choiceDefinitionId });
      return;
    }
    this.logService.debug('SyncChoiceDefinition.Starting', { syncRequestCount: syncRequests.length, choiceDefinitionId, fullSync });

    const populatedRequests = fullSync ? syncRequests : await this.populateSyncRequests(syncRequests);
    await this.fetchAndProcessChoiceDefinitions(populatedRequests, fullSync);

    if (choiceDefinitionId == null) { // Only clean up orphans when syncing all
      await this.deleteUnreferencedChoiceDefinitions(syncRequests.map(r => r.ChoiceDefinitionId));
    }

    this.logService.debug('SyncChoiceDefinition.Completed', { syncRequestCount: syncRequests.length, choiceDefinitionId, fullSync });
  }

  /**
   * Build unique sync requests for every referenced choice definition across all form definitions.
   */
  private buildSyncRequests(formDefinitions: FormDefinition[]): ChoiceSyncRequest[] {
    const syncRequestMap = new Map<number, ChoiceSyncRequest>();
    for (const form of formDefinitions) {
      const projectId = form.projectId;
      form.steps?.forEach(step => step.controls?.forEach(control => this.extractSyncRequestsFromControl(syncRequestMap, control, projectId)));
    }

    return Array.from(syncRequestMap.values());
  }

  /** Recursively walks a control tree collecting choice definition IDs */
  private extractSyncRequestsFromControl(syncRequestMap: Map<number, ChoiceSyncRequest>, control: FormControl | undefined, projectId: number): void {
    if (!control?.type) return;
    const { type } = control;
    if (type.inputType === FORM_CONTROL_TYPES.GROUP && Array.isArray(type.groupControls)) {
      type.groupControls.forEach(child => this.extractSyncRequestsFromControl(syncRequestMap, child, projectId));
      return;
    }
    if (type.inputType === FORM_CONTROL_TYPES.CHOICE && type.inputMode) {
      this.addChoiceDefinitionToMap(syncRequestMap, type.inputMode, projectId);
    }
  }

  /** Adds a choice definition reference to the accumulating map */
  private addChoiceDefinitionToMap(syncRequestMap: Map<number, ChoiceSyncRequest>, rawInputMode: string | number, projectId: number): void {
    if (rawInputMode == null) return;
    const choiceDefinitionId = Number(rawInputMode);
    if (Number.isNaN(choiceDefinitionId)) return; // Ignore non-numeric modes

    let existing = syncRequestMap.get(choiceDefinitionId);
    if (!existing) {
      existing = { ChoiceDefinitionId: choiceDefinitionId, ProjectIds: [projectId], CurrentChoiceIds: [] };
      syncRequestMap.set(choiceDefinitionId, existing);
    } else if (!existing.ProjectIds.includes(projectId)) {
      existing.ProjectIds.push(projectId);
    }
  }

  /**
   * Populate each request with the local state (existing choice IDs & latest modified date) to enable incremental server responses.
   */
  private async populateSyncRequests(requests: ChoiceSyncRequest[]): Promise<ChoiceSyncRequest[]> {
    for (const request of requests) {
      let existingDefinitionMetadata: ChoiceDefinitionMetadata;

      try {
        existingDefinitionMetadata = await this.choiceDefinitionsService.getOneMetadata(request.ChoiceDefinitionId);
      } catch (e) {
        if (e instanceof RecordNotFoundException) {
          this.logService.debug('SyncChoiceDefinition.MetadataNotFound', { choiceDefinitionId: request.ChoiceDefinitionId });
          continue; // No existing metadata, so nothing to populate
        }
        this.logService.warn('SyncChoiceDefinition.MetadataFetchError', { choiceDefinitionId: request.ChoiceDefinitionId, error: (e as Error).message });
        continue;
      }

      request.CurrentChoiceIds = existingDefinitionMetadata.choices.map(c => c.id) ?? [];

      let latest: string | undefined;
      for (const { modifiedDate } of existingDefinitionMetadata.choices) {
        if (!latest || modifiedDate > latest)
          latest = modifiedDate;
      }
      request.LatestModifiedDate = latest;
    }

    return requests;
  }

  /**
   * Fetch, merge, and store choice definitions.
   */
  private async fetchAndProcessChoiceDefinitions(requests: ChoiceSyncRequest[], fullSync: boolean = false): Promise<void> {
    if (!requests.length) return;
    const limit = pLimit(SyncChoiceDefinitionService.CHOICE_SYNC_CONCURRENCY);
    const failedRequests: ChoiceSyncRequest[] = [];

    const tasks = requests.map(request => limit(async () => {
      try {
        const response = await this.http.post<ChoiceDefinitionSyncResponse>(this.FLEX_FORMS_API_ENDPOINTS.SyncChoices, [request]).toPromise();

        for (const downloadedChoiceDefinitions of response.choiceDefinitions) {
          let upsertedChoiceDefinition: ChoiceDefinition;
          if (!fullSync) { 
            // Quick sync - merge with existing
            upsertedChoiceDefinition = await this.mergeChoiceDeltas(downloadedChoiceDefinitions);
          }
          else { 
            // Full sync - replace existing
            upsertedChoiceDefinition = downloadedChoiceDefinitions;
          }

          await this.choiceDefinitionsService.insertOne(upsertedChoiceDefinition);
          this.logService.debug('SyncChoiceDefinition.Synced', { 
            id: upsertedChoiceDefinition.id, 
            upserts: downloadedChoiceDefinitions.choices.filter(c => c.action === 'UPSERT').length, 
            deletes: downloadedChoiceDefinitions.choices.filter(c => c.action === 'DELETE').length, 
            newTotal: upsertedChoiceDefinition.choices.length
          });
        }
      } catch (err) {
        this.logService.error('SyncChoiceDefinition.FetchFailure', err, { request });
        failedRequests.push(request);
      }
    }));

    await Promise.all(tasks);

    if (failedRequests.length > 0) {
      await this.handleFailedChoiceDefinitionSyncs(failedRequests, fullSync);
    }
  }

  /**
   * Present user-facing alert for failed choice definition sync attempts.
   */
  private async handleFailedChoiceDefinitionSyncs(failedRequests: ChoiceSyncRequest[], fullSync: boolean): Promise<void> {
    if (!failedRequests.length) return;
    const nameLookups = await Promise.all(failedRequests.map(async request => {
      try {
        const metadata = await this.choiceDefinitionsService.getOneMetadata(request.ChoiceDefinitionId);
        return metadata.name + " (ID: " + request.ChoiceDefinitionId.toString() + ")";
      } catch (e) {
        return "Unknown (ID: " + request.ChoiceDefinitionId.toString() + ")";
      }
    }));

    const failedListItems = nameLookups.map(name => `<li>${name}</li>`).join('');

    await this.notificationService.showAlert({
      header: 'Choice Sync Issues',
      message: `<p>The following choice lists failed to update:</p>
                <ul>${failedListItems}</ul>
                <p>The app will continue using previous values.</p>`,
      buttons: [
        { text: 'Retry Failed', handler: () => this.retryFailedChoiceDefinitions(failedRequests, fullSync) },
        { text: 'OK' }
      ],
      suppression: {
        suppressKey: GlobalConstants.SUPPRESS_INVALID_SYNC_ALERT_DATE,
        suppressDuration: 24,
        suppressLabel: 'Hide this message'
      }
    });
  }

  /** Guarded retry logic to avoid duplicate concurrent retries. */
  private retryFailedChoiceDefinitions(failedRequests: ChoiceSyncRequest[], fullSync: boolean): boolean | void {
    if (this.choiceDefinitionRetryInProgress) {
      this.logService.debug('SyncChoiceDefinition.RetryIgnoredAlreadyInProgress');
      return false; // Keep alert open if user spams button
    }
    this.choiceDefinitionRetryInProgress = true;
    this.fetchAndProcessChoiceDefinitions(failedRequests, fullSync)
      .finally(() => { this.choiceDefinitionRetryInProgress = false; });
  }

  /**
   * Merge downloaded definitions with existing local data, handling deletions and updates.
   */
  private async mergeChoiceDeltas(downloadedChoiceDefinition: ChoiceDefinition): Promise<ChoiceDefinition> {
    const existingChoiceDefinition = await this.choiceDefinitionsService.getOne(downloadedChoiceDefinition.id);
    const deltaIds = new Set<number>(downloadedChoiceDefinition.choices.map(c => c.id));
    const survivors = existingChoiceDefinition?.choices.filter(c => !deltaIds.has(c.id)) ?? [];  // Keep any existing choices not mentioned in the delta. Deletes will be in delta as "DELETE" actions and therefore not preserved.
    const upserts = downloadedChoiceDefinition.choices.filter(c => c.action === 'UPSERT');
    const mergedChoices = survivors.concat(upserts);
    return {
      RecordId: existingChoiceDefinition?.RecordId ?? null,
      id: downloadedChoiceDefinition.id,
      name: downloadedChoiceDefinition.name,
      choices: mergedChoices
    } as ChoiceDefinition;
  }

  /**
   * Remove choice definitions that are no longer referenced by any form.
   * Warning: only call this when all active choice definitions are being synced, never when syncing a single definition.
   */
  private async deleteUnreferencedChoiceDefinitions(activeChoiceDefinitionIds: number[]) {
    const localChoiceDefinitionMetadata = await this.choiceDefinitionsService.getAllMetadata();
    const unreferencedChoiceDefinitions = localChoiceDefinitionMetadata.filter(cd => !activeChoiceDefinitionIds.includes(cd.id));
    
    if (unreferencedChoiceDefinitions.length > 0) {
      await this.choiceDefinitionsService.deleteMany(unreferencedChoiceDefinitions);
      this.logService.debug('SyncChoiceDefinition.DeletedUnreferenced', { choiceDefinitionIds: unreferencedChoiceDefinitions.map(cd => cd.id) });
    }
  }
}
